import {
  ChangeDetectionStrategy,
  Component,
  computed,
  ElementRef,
  inject,
  input,
  linkedSignal,
  output,
  viewChild
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatIcon } from '@angular/material/icon';
import { LiveFormsTranslateLoaderService } from '../../services/live-forms-translate-loader.service';
import { FormsModule } from '@angular/forms';
import { debounce } from 'lodash';

export interface MenuAction {
  action:
    | 'addComment'
    | 'addPhotoComment'
    | 'deleteItem'
    | 'getXml'
    | 'getFormattedXml'
    | 'setInputColor'
    | 'removeInputColor'
    | 'setSelectionColor'
    | 'removeSelectionColor';
  data?: any;
}

@Component({
  selector: 'lfe-item-menu',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, MatIcon, FormsModule],
  templateUrl: './item-menu.component.html',
  standalone: true,
  styleUrl: './item-menu.component.css'
})
export class ItemMenuComponent {
  isVisible = input<boolean>(false);
  canDeleteComment = input<boolean>(false);
  itemInputColorControlEnabled = input.required<boolean>();
  itemInputColor = input<string>('');
  itemSelectionColorControlEnabled = input.required<boolean>();
  itemSelectionColor = input<string>('');
  debug = input<boolean>(false);

  menuAction = output<MenuAction>();
  menuClose = output<void>();

  colorPicker = viewChild<ElementRef>('colorPicker');
  colorPickerTable = viewChild<ElementRef>('colorPickerTable');

  private translateService = inject(LiveFormsTranslateLoaderService);

  itemInputColorLive = linkedSignal(() => this.itemInputColor());
  itemSelectionColorLive = linkedSignal(() => this.itemSelectionColor());

  isRemoveInputColorEnabled = computed(() => this.itemInputColorLive());

  showDeleteOption = computed(() => {
    return this.canDeleteComment();
  });

  // Translation getters
  get addCommentText(): string {
    return this.translateService.instant('liveforms-editor.item-menu.actions.add-comment');
  }

  get addPhotoCommentText(): string {
    return this.translateService.instant('liveforms-editor.item-menu.actions.add-photo-comment');
  }

  get setInputColor(): string {
    return this.translateService.instant('liveforms-editor.item-menu.actions.set-input-color');
  }

  get removeInputColor(): string {
    return this.translateService.instant('liveforms-editor.item-menu.actions.remove-input-color');
  }

  get setSelectionColor(): string {
    return this.translateService.instant('liveforms-editor.item-menu.actions.set-fill-color');
  }

  get removeSelectionColor(): string {
    return this.translateService.instant('liveforms-editor.item-menu.actions.clear-fill-color');
  }

  get deleteItemText(): string {
    return this.translateService.instant('liveforms-editor.item-menu.actions.delete-item');
  }

  get getXmlText(): string {
    return this.translateService.instant('liveforms-editor.item-menu.debug.get-xml');
  }

  get getFormattedXmlText(): string {
    return this.translateService.instant('liveforms-editor.item-menu.debug.get-formatted-xml');
  }

  onAddComment(): void {
    this.menuAction.emit({ action: 'addComment' });
    this.menuClose.emit();
  }

  onAddPhotoComment(): void {
    this.menuAction.emit({ action: 'addPhotoComment' });
    this.menuClose.emit();
  }

  onOpenInputColorPicker(): void {
    this.colorPicker()?.nativeElement.focus();
    this.colorPicker()?.nativeElement.click();
  }

  debouncedSetInputColor = debounce((color: string) => {
    this.itemInputColorLive.set(color);
    this.menuAction.emit({ action: 'setInputColor', data: color });
  }, 300);

  onInputColorChange(event: Event): void {
    this.debouncedSetInputColor((event.target as HTMLInputElement).value);
  }

  onRemoveInputColor(): void {
    if (this.isRemoveInputColorEnabled()) {
      this.menuAction.emit({ action: 'removeInputColor' });
      this.itemInputColorLive.set('');
      this.menuClose.emit();
    }
  }

  onOpenTableColorPicker(): void {
    this.colorPickerTable()?.nativeElement.focus();
    this.colorPickerTable()?.nativeElement.click();
  }

  debouncedSetSelectionColor = debounce((color: string) => {
    this.itemSelectionColorLive.set(color);
    this.menuAction.emit({ action: 'setSelectionColor', data: color });
  }, 300);

  onSelectionColorChange(event: Event): void {
    this.debouncedSetSelectionColor((event.target as HTMLInputElement).value);
  }

  onRemoveSelectionColor(): void {
    this.menuAction.emit({ action: 'removeSelectionColor' });
    this.menuClose.emit();
  }

  onDeleteItem(): void {
    this.menuAction.emit({ action: 'deleteItem' });
  }

  onGetXml(): void {
    this.menuAction.emit({ action: 'getXml' });
    this.menuClose.emit();
  }

  onGetFormattedXml(): void {
    this.menuAction.emit({ action: 'getFormattedXml' });
    this.menuClose.emit();
  }

  onMenuClose(): void {
    this.menuClose.emit();
  }
}
